import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSelect from "@/components/LanguageSelect";
import { UserRound, Menu, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

export default function PublicHeader() {
  const { partner } = useAuth();
  const { t } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // SPA navigation (no full reload). Hash links scroll to the section, landing
  // on "/" first if we're on another page.
  const go = (to) => {
    setOpen(false);
    if (to.includes("#")) {
      const [path, hash] = to.split("#");
      const target = path || "/";
      if (pathname === target) {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate(target);
        setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 450);
      }
    } else {
      navigate(to);
    }
  };
  const NavItem = ({ to, className, children, testid }) =>
    to.includes("#")
      ? <button type="button" onClick={() => go(to)} data-testid={testid} className={className}>{children}</button>
      : <Link to={to} onClick={() => setOpen(false)} data-testid={testid} className={className}>{children}</Link>;
  const primaryLinks = [
    { to: "/annonces", label: t("nav.ads") },
    { to: "/objets-trouves", label: t("nav.found") },
    { to: "/carte", label: t("nav.map") },
    { to: "/partners", label: t("nav.partners") },
    { to: "/#pricing", label: t("nav.pricing") },
  ];
  const moreLinks = [
    { to: "/classement", label: t("nav.ranking") },
    { to: "/track", label: t("nav.track") },
    { to: "/#how", label: t("nav.how") },
  ];
  const links = [...primaryLinks, ...moreLinks];
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-border">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 h-16 flex items-center gap-4">
        <Link to="/" data-testid="header-home-link" className="flex items-center shrink-0">
          <Logo size={32} withText textClass="hidden sm:inline-flex text-lg sm:text-xl text-slate-900" />
        </Link>
        <nav className="hidden lg:flex flex-1 items-center justify-center gap-5 xl:gap-6" data-testid="desktop-nav">
          {primaryLinks.map((l) => (
            <NavItem key={l.to} to={l.to} testid={`nav-link-${l.to.replace(/[/#]/g, "") || "home"}`}
              className={`text-sm font-semibold whitespace-nowrap transition-colors ${pathname === l.to ? "text-findo-navy" : "text-slate-600 hover:text-slate-900"}`}>
              {l.label}
            </NavItem>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-more-trigger" className="flex items-center gap-1 text-sm font-semibold whitespace-nowrap text-slate-600 hover:text-slate-900 transition-colors outline-none">
                {t("nav.more")} <ChevronDown size={15} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="rounded-xl" data-testid="nav-more-menu">
              {moreLinks.map((l) => (
                <DropdownMenuItem key={l.to} asChild>
                  <NavItem to={l.to} testid={`nav-more-link-${l.to.replace(/[/#]/g, "") || "home"}`} className="w-full text-left cursor-pointer text-sm font-semibold text-slate-700">{l.label}</NavItem>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3 ml-auto shrink-0">
          <LanguageSelect variant="compact" testid="language-select-header" />
          <ThemeToggle />
          <Link to="/mon-compte" className="hidden lg:block" data-testid="header-account-link">
            <Button variant="ghost" data-testid="header-individual-btn" className="rounded-full font-semibold h-9 px-4 text-sm whitespace-nowrap">{t("nav.individual")}</Button>
          </Link>
          {partner ? (
            <Link to="/b2b/registry">
              <Button data-testid="header-dashboard-btn" className="rounded-full font-semibold h-9 px-3 sm:px-4 text-sm whitespace-nowrap">{t("nav.dashboard")}</Button>
            </Link>
          ) : (
            <Link to="/auth/login" className="hidden lg:block">
              <Button data-testid="header-login-btn" className="rounded-full font-semibold h-9 px-4 text-sm whitespace-nowrap">{t("nav.business")}</Button>
            </Link>
          )}

          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden h-10 w-10 grid place-items-center rounded-full border border-border text-slate-700 hover:bg-slate-50" aria-label="Menu" data-testid="header-mobile-menu-btn">
                <Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm flex flex-col" data-testid="header-mobile-menu">
              <SheetHeader>
                <SheetTitle className="text-left"><Logo size={30} withText textClass="text-lg text-slate-900" /></SheetTitle>
              </SheetHeader>
              <nav className="mt-4 flex flex-col gap-1" data-testid="mobile-nav-links">
                {links.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <NavItem to={l.to} className="w-full text-left px-3 py-3 rounded-xl text-base font-semibold text-slate-700 hover:bg-slate-100 transition-colors">
                      {l.label}
                    </NavItem>
                  </SheetClose>
                ))}
                <div className="my-2 h-px bg-border" />
                <SheetClose asChild>
                  <Link to="/mon-compte" className="px-3 py-3 rounded-xl text-base font-semibold text-slate-700 hover:bg-slate-100 flex items-center gap-2">
                    <UserRound size={18} /> {t("nav.individual")}
                  </Link>
                </SheetClose>
                {!partner && (
                  <SheetClose asChild>
                    <Link to="/auth/login" className="px-3 py-3 rounded-xl text-base font-semibold text-slate-700 hover:bg-slate-100" data-testid="mobile-business-link">
                      {t("nav.business")}
                    </Link>
                  </SheetClose>
                )}
              </nav>
              <div className="mt-auto pt-4 border-t border-border flex items-center justify-between">
                <ThemeToggle />
                {partner && (
                  <SheetClose asChild><Link to="/b2b/registry"><Button className="rounded-full font-semibold">{t("nav.dashboard")}</Button></Link></SheetClose>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
